import React, { useState, useEffect, createContext } from "react";
import { calculateSundays, isJsonString } from "../helpers/helpers";
import axios from "axios";
import {
  CabinetContextValue,
  CabinetProviderProps,
  Sunday,
} from "../helpers/types";

const defaultState = {
  year: 0,
  week: 0,
  sundays: [],
  fetchEvent: () => {},
  thoughts: [],
  setYear: () => {},
  setWeek: () => {},
  setThoughts: () => {},
  sundaysWithThoughts: [],
  setSundaysWithThoughts: () => {},
  navigate: null,
  setNavigate: () => {},
  grabbedData: false,
  view: "calendar",
  setView: () => {},
  themes: [],
  updateThoughts: () => {},
};

const CabinetContext =
  createContext<Partial<CabinetContextValue>>(defaultState);

const CabinetProvider: React.FC<CabinetProviderProps> = ({ children }) => {
  const [week, setWeek] = useState<number>(0);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [minYear] = useState<number>(2023);
  const [sundays, setSundays] = useState<any>([]);
  const [navigate, setNavigate] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [grabbedData, setGrabbedData] = useState<boolean>(false);
  const [thoughts, setThoughts] = useState<any[]>([]);
  const [view, setView] = useState<string>("calendar");
  const [themes, setThemes] = useState<any[]>([]);
  const [sundaysWithThoughts, setSundaysWithThoughts] = useState<any[]>([]);

  useEffect(() => {
    const sundays = calculateSundays(minYear, year);
    // const latestWeek = sundays.length - 1;
    setSundays(sundays);
    // setWeek(latestWeek);
    fetchThemes();
    fetchSundaysWithThoughts();
  }, []);

  useEffect(() => {
    if (view !== "calendar") {
      fetchThoughtsByTheme(view);
    } else {
      if (sundaysWithThoughts) {
        fetchThoughtsByDate(new Date(sundaysWithThoughts[week]));
      }
      updateThoughts(sundaysWithThoughts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const fetchEvent = (
    setDisabledLeft: (arg0: boolean) => void,
    setDisabledRight: (arg0: boolean) => void,
    searchParams: any,
    setDisplayTopic: (arg0: boolean) => void
  ) => {
    setLoading!(true);
    if (!sundaysWithThoughts) return;
    const setDateParams = (newWeek: number) => {
      if (setWeek) setWeek(newWeek);
      setDisabledLeft(newWeek === 0);
      setDisabledRight(
        sundaysWithThoughts ? newWeek === sundaysWithThoughts.length - 1 : true
      );
    };
    let found = false;
    for (const [key, value] of searchParams.entries()) {
      if (key === "date") {
        found = true;

        // make year-mm-dd
        const date = new Date(value);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const formattedDate = `${year}-${month
          .toString()
          .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;

        const newWeek = sundaysWithThoughts.findIndex(
          (sunday: string) => formattedDate === sunday
        );

        if (newWeek > -1) {
          setDateParams(newWeek);
        }
      }
      if (key === "theme") {
        found = true;
        setView!(value);
        setDisplayTopic(true);
      }
    }
    if (!found) {
      setDateParams(sundaysWithThoughts.length - 1);
      setWeek!(sundaysWithThoughts.length - 1);
    }
    setLoading!(false);
  };

  const fetchSundaysWithThoughts = async () => {
    try {
      const res = await axios.get(
        "https://us-central1-thought-cabinet.cloudfunctions.net/app/getSundaysWithThoughts"
      );
      // sort by date
      res.data.sundays.sort((a: string, b: string) => {
        return new Date(a).getTime() - new Date(b).getTime();
      });
      setSundaysWithThoughts(res.data.sundays);
      setWeek(res.data.sundays.length - 1);
    } catch (error) {
      console.error("Error fetching Sundays with thoughts:", error);
    }
  };

  const fetchThoughtsByDate = async (day: Date | string) => {
    setGrabbedData(false);

    // Ensure 'day' is a Date object
    if (typeof day === "string") {
      day = new Date(day);
    }

    if (isNaN(day.getTime())) {
      return;
    }

    let date = day.toISOString();
    try {
      const res = await axios.post(
        "https://us-central1-thought-cabinet.cloudfunctions.net/app/getThoughtsByDate",
        {
          sunday: date,
        }
      );
      res.data.thoughts.forEach((thought: any) => {
        for (const key in thought) {
          if (isJsonString(thought[key])) {
            thought[key] = JSON.parse(thought[key]);
          }
        }
      });
      setThoughts(res.data.thoughts);
      setGrabbedData(true);
      res.data = null;
    } catch (error) {
      console.error("Error fetching thoughts:", error);
    }
  };

  const fetchThoughtsByTheme = async (theme: string) => {
    setLoading(true);
    setGrabbedData(false);
    try {
      const res = await axios.post(
        "https://us-central1-thought-cabinet.cloudfunctions.net/app/getThoughtsByTheme",
        {
          theme: theme,
        }
      );
      res.data.thoughts.forEach((thought: any) => {
        for (const key in thought) {
          if (isJsonString(thought[key])) {
            thought[key] = JSON.parse(thought[key]);
          }
        }
      });
      res.data.thoughts.forEach((thought: any) => {
        thought.recent = 1;
        let labels = thought.label.split(", ");
        labels = labels.filter((label: string) => label !== theme);
        thought.label = labels.join(", ");
      });
      setThoughts(res.data.thoughts);
      setGrabbedData(true);
      setLoading(false);
      res.data = null;
    } catch (error) {
      console.error("Error fetching thoughts:", error);
    }
  };

  const fetchThemes = async () => {
    try {
      const res = await axios.get(
        "https://us-central1-thought-cabinet.cloudfunctions.net/app/getAllThemes"
      );

      // res.data.themes.forEach((theme: string, index: number) => {
      //   res.data.themes[index] = theme.trim();
      // });

      const themes = Array.from(new Set(res.data.themes));
      setThemes(themes);
      res.data = null;
    } catch (error) {
      console.error("Error fetching themes:", error);
    }
  };

  useEffect(() => {
    if (view === "calendar") {
      updateThoughts(sundaysWithThoughts);
      // fetchSundaysWithThoughts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week, loading, view]);

  const updateThoughts = (sundaysWithThoughts: any[]) => {
    if (sundaysWithThoughts.length > 0 && !loading) {
      const currentSunday = sundaysWithThoughts[week];
      // const sundayDate = new Date(
      //   currentSunday[0] + " " + currentSunday[1] + " " + currentSunday[2]
      // );
      // console.log(sundayDate);

      fetchThoughtsByDate(currentSunday);
    }
  };

  return (
    <CabinetContext.Provider
      value={{
        year,
        week,
        sundays,
        thoughts,
        setYear,
        setWeek,
        setThoughts: setThoughts,
        sundaysWithThoughts,
        setSundaysWithThoughts,
        navigate,
        setNavigate,
        // loading,
        // setLoading,
        grabbedData,
        view,
        setView,
        themes,
        updateThoughts,
        fetchEvent,
      }}
    >
      {children}
    </CabinetContext.Provider>
  );
};

export default CabinetProvider;
export { CabinetContext, CabinetProvider };
